import { InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import type { DiagnosesQuery } from '../../../graphql/generated/graphql.ts';
import { DIAGNOSES_QUERY } from './Diagnoses.gql';
import { CreateDiagnoseModal } from './diagnoses/CreateDiagnoseModal';
import { UpdateDiagnoseModal } from './diagnoses/UpdateDiagnoseModal';

export type Diagnose = NonNullable<DiagnosesQuery['labDiagnoses']>[number];

export const Diagnoses: React.FC = () => {
  const { token } = theme.useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editDiagnose, setEditDiagnose] = useState<Diagnose | null>(null);

  const { data, loading, refetch } = useQuery(DIAGNOSES_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<Diagnose> = [
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 150,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Text',
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
      sorter: (a, b) => a.text.localeCompare(b.text),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'ICD-10',
      dataIndex: 'icd10',
      key: 'icd10',
      ellipsis: true,
      sorter: (a, b) => a.icd10.localeCompare(b.icd10),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </span>
      ),
    },
  ];

  const filteredDiagnoses: Diagnose[] = useMemo(
    () => filterByTokens(data?.labDiagnoses ?? [], debouncedSearch, [d => d.name, d => d.text, d => d.icd10]),
    [debouncedSearch, data]
  );

  return (
    <MainContent>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          autoFocus
          allowClear
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Name oder Text">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalOpen(true)}>
          Neue Diagnose
        </Button>
      </div>
      <Table<Diagnose>
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredDiagnoses}
        onRow={record => ({ onClick: () => setEditDiagnose(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Diagnosen`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateDiagnoseModal
        open={createModalOpen}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalOpen(false)}
        labId={labId ?? ''}
        diagnoses={data?.labDiagnoses ?? []}
      />
      <UpdateDiagnoseModal
        diagnose={editDiagnose}
        onSuccess={() => refetch()}
        onClose={() => setEditDiagnose(null)}
        diagnoses={data?.labDiagnoses ?? []}
      />
    </MainContent>
  );
};
